import React from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Divider,
    Grid,
    List,
    ListItem,
    ListItemText,
    Paper,
    Stack,
    Typography
} from '@mui/material';
import PublishControls from './PublishControls';
import VersionHistory from './VersionHistory';
import { formatMobilityLinePricingNote } from '../fields/mobilityLinePricing';
import {
    normalizeDisplayChipList,
    safeChipColor,
    sortedDisplayChips
} from '../fields/displayMetadata';
import { getLifecycleStatus, lifecycleChipColor } from '../fields/lifecycleMetadata';

function ProductPricingActiveTab({ context }) {
    const {
        activeEmailShell,
        activeTab,
        chipColor,
        config,
        counts,
        draftConfig,
        draftEmailShell,
        draftValidation,
        emailShellDraftChanged,
        emailShellImageFields,
        findActiveCatalogProduct,
        findActiveHomePhoneProduct,
        findActiveInternetProduct,
        findActiveTvDisplayPackage,
        formatBoolean,
        formatDateTime,
        formatMoney,
        futureControlCards,
        getCatalogChangedFields,
        getHomePhoneChangedFields,
        getHomePhoneDisplayMetadata,
        getHomePhoneGroup,
        getInternetChangedFields,
        getInternetDisplayMetadata,
        getInternetProducts,
        getMobilityAdminGroup,
        getTvDisplayChangedFields,
        getTvDisplayPackages,
        hasDraftChangesForScope,
        isCatalogDraftChanged,
        isHomePhoneDraftChanged,
        isInternetDraftChanged,
        isTvDisplayDraftChanged,
        mobilityAdminFilter,
        mobilityAdminFilters,
        normalizeArray,
        openCatalogEditor,
        openEmailShellEditor,
        openHomePhoneEditor,
        openInternetEditor,
        openNewCatalogEditor,
        openNewHomePhoneEditor,
        openNewInternetEditor,
        openPublishDialog,
        openRollbackDialog,
        openTvDisplayEditor,
        renderCategoryDraftStatus,
        renderDraftMessages,
        renderFutureControls,
        renderSectionIntro,
        renderStorageStatus,
        scopeLabel,
        setEmailShellPreviewOpen,
        setMobilityAdminFilter,
        setShowAllValidationWarnings,
        setShowMoreVersions,
        setVersionHistoryScope,
        shortText,
        showAllValidationWarnings,
        showMoreVersions,
        sortedVersions,
        sortProductsBySortOrder,
        tvToolLinks,
        tvV2PriceByKey,
        tvV2Pricing,
        validateDraftConfig,
        validatingDraft,
        versionHistoryScope
    } = context;

    const renderStatusChips = (product) => (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            <Chip label={`Sort ${product.sortOrder ?? 'N/A'}`} size="small" variant="outlined" />
            <Chip label={`Status: ${getLifecycleStatus(product)}`} size="small" color={lifecycleChipColor(getLifecycleStatus(product))} variant="outlined" />
            <Chip label={product.visible === false ? 'Hidden' : 'Visible'} size="small" color={product.visible === false ? 'warning' : 'success'} variant="outlined" />
            <Chip label={product.visibleInAdminBuilder === false ? 'Admin hidden' : 'Admin visible'} size="small" variant="outlined" />
            <Chip label={product.visibleInAgentBuilder === false ? 'Agent hidden' : 'Agent visible'} size="small" variant="outlined" />
            <Chip label={product.quoteEligible === false ? 'Not quote eligible' : 'Quote eligible'} size="small" variant="outlined" />
            <Chip label={product.customerEmailEligible ? 'Customer email eligible' : 'Quote-only email'} size="small" color={product.customerEmailEligible ? 'success' : 'default'} variant="outlined" />
            {product.separateFlowOnly && <Chip label="Separate flow only" size="small" color="warning" variant="outlined" />}
            {normalizeArray(product.badges).map((badge) => (
                <Chip key={badge} label={badge} size="small" color={safeChipColor(product.badgeColor)} variant="outlined" />
            ))}
            {product.hot && <Chip label="Hot" size="small" color="error" variant="outlined" />}
            {product.recommended && <Chip label="Recommended" size="small" color="success" variant="outlined" />}
        </Stack>
    );

    const renderKeyStatusChips = (product, changed) => {
        const lifecycleStatus = getLifecycleStatus(product);
        const expiresAt = product.expiresAt ? new Date(product.expiresAt) : null;
        const expiresToday = expiresAt && !Number.isNaN(expiresAt.getTime()) && expiresAt.toDateString() === new Date().toDateString();

        return (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {changed && <Chip label="Draft changed" size="small" color="warning" />}
                <Chip label={lifecycleStatus.charAt(0).toUpperCase() + lifecycleStatus.slice(1)} size="small" color={lifecycleChipColor(lifecycleStatus)} variant="outlined" />
                {expiresToday && lifecycleStatus !== 'expired' && <Chip label="Expires today" size="small" color="warning" variant="outlined" />}
                {(product.isDraftOnly || product.neverPublished) && <Chip label="Draft new" size="small" color="info" variant="outlined" />}
                <Chip label={product.visible === false ? 'Hidden' : 'Visible'} size="small" color={product.visible === false ? 'warning' : 'success'} variant="outlined" />
                <Chip label={product.visibleInAgentBuilder === false ? 'Agent hidden' : 'Agent-visible'} size="small" color={product.visibleInAgentBuilder === false ? 'warning' : 'success'} variant="outlined" />
                <Chip label={product.quoteEligible === false ? 'Not quote eligible' : 'Quote eligible'} size="small" variant="outlined" />
                <Chip label={product.customerEmailEligible ? 'Customer email eligible' : 'Email excluded'} size="small" color={product.customerEmailEligible ? 'success' : 'default'} variant="outlined" />
                {product.separateFlowOnly && <Chip label="Separate flow only" size="small" color="warning" variant="outlined" />}
            </Stack>
        );
    };

    const renderAgentFacingChips = (product, display) => {
        const topBadges = (display?.topBadges || []).filter((chip) => chip.visible && chip.text);
        const detailChips = (display?.detailChips || display?.chips || []).filter((chip) => chip.visible && chip.text);
        const chips = [
            ...topBadges.map((chip) => ({
                text: chip.text,
                color: chipColor(chip.type),
                variant: 'outlined'
            })),
            ...detailChips.map((chip) => ({
                text: chip.text,
                color: chipColor(chip.type),
                variant: 'outlined'
            }))
        ].filter(Boolean);

        if (chips.length === 0) {
            return (
                <Typography variant="caption" color="text.secondary">
                    No agent-facing card chips configured.
                </Typography>
            );
        }

        return (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {chips.map((chip, index) => (
                    <Chip
                        key={`${chip.text}-${index}`}
                        label={chip.text}
                        size="small"
                        color={chip.color}
                        variant={chip.variant}
                    />
                ))}
            </Stack>
        );
    };

    const renderProductCardSummary = (product, onEdit) => {
        const referencePrice = getReferencePrice(product);

        return (
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h6">{product.displayName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                        Sort order {product.sortOrder ?? 'N/A'}
                    </Typography>
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-end', sm: 'center' }}>
                    <Box textAlign="right">
                        <Typography variant="h6" color="primary.main">{formatMoney(getPrimaryPrice(product))}</Typography>
                        {referencePrice !== undefined && referencePrice !== null && (
                            <Typography variant="caption" color="text.secondary">
                                Ref {formatMoney(referencePrice)}
                            </Typography>
                        )}
                    </Box>
                    <Button variant="outlined" size="small" onClick={onEdit}>
                        Edit Draft
                    </Button>
                </Stack>
            </Stack>
        );
    };

    const getPrimaryPrice = (product) => (
        product.belowLinePrice !== undefined ? product.belowLinePrice : product.price
    );

    const getReferencePrice = (product) => (
        product.aboveLinePrice !== undefined ? product.aboveLinePrice : product.regularPrice
    );

    const renderDetailsPanel = (children) => (
        <Box
            component="details"
            sx={{
                '& summary': {
                    cursor: 'pointer',
                    color: 'primary.main',
                    fontWeight: 700,
                    fontSize: '0.875rem'
                },
                '&[open]': {
                    mt: 0.5
                }
            }}
        >
            <Box component="summary">Details</Box>
            <Stack spacing={1.25} sx={{ mt: 1.25 }}>
                {children}
            </Stack>
        </Box>
    );

    const renderPriceFields = (product) => {
        const fields = [
            product.belowLinePrice !== undefined ? ['Below-line/PAB price', formatMoney(product.belowLinePrice)] : null,
            product.aboveLinePrice !== undefined ? ['Above-line price', formatMoney(product.aboveLinePrice)] : null,
            product.mobilityBelowLinePrice !== undefined ? ['Mobility discount price', formatMoney(product.mobilityBelowLinePrice)] : null,
            product.eppMobilityBelowLinePrice !== undefined ? ['EPP with mobility price', formatMoney(product.eppMobilityBelowLinePrice)] : null,
            product.price !== undefined ? ['Price', formatMoney(product.price)] : null,
            product.regularPrice !== undefined ? ['Regular/reference price', formatMoney(product.regularPrice)] : null,
            product.requiredFees !== undefined ? ['Required fees', formatMoney(product.requiredFees)] : null
        ].filter(Boolean);

        if (fields.length === 0) {
            return null;
        }

        return (
            <Grid container spacing={1}>
                {fields.map(([label, value]) => (
                    <Grid item xs={12} sm={6} key={label}>
                        <Typography variant="caption" color="text.secondary">{label}</Typography>
                        <Typography variant="body2" fontWeight={700}>{value}</Typography>
                    </Grid>
                ))}
            </Grid>
        );
    };

    const renderNotes = (title, notes) => {
        if (!notes || notes.length === 0) {
            return null;
        }

        return (
            <Box>
                <Typography variant="caption" color="text.secondary">{title}</Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                    {notes.map((note) => (
                        <Chip key={note} label={note} size="small" variant="outlined" />
                    ))}
                </Stack>
            </Box>
        );
    };

    const renderHomePhoneActiveDraftDiff = (draftProduct, activeProduct) => {
        const changedFields = getHomePhoneChangedFields(draftProduct, activeProduct);

        if (changedFields.length === 0) {
            return null;
        }

        return (
            <Alert severity="warning" variant="outlined" sx={{ py: 0.75 }}>
                <Typography variant="body2" fontWeight={700}>Draft differs from active</Typography>
                <Typography variant="caption" display="block">
                    Changed: {changedFields.join(', ')}
                </Typography>
                <Grid container spacing={1} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Active price</Typography>
                        <Typography variant="body2">{formatMoney(activeProduct?.price)}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Draft price</Typography>
                        <Typography variant="body2" fontWeight={700}>{formatMoney(draftProduct?.price)}</Typography>
                    </Grid>
                </Grid>
            </Alert>
        );
    };

    const renderInternetActiveDraftDiff = (draftProduct, activeProduct) => {
        const changedFields = getInternetChangedFields(draftProduct, activeProduct);

        if (changedFields.length === 0) {
            return null;
        }

        return (
            <Alert severity="warning" variant="outlined" sx={{ py: 0.75 }}>
                <Typography variant="body2" fontWeight={700}>Draft differs from active</Typography>
                <Typography variant="caption" display="block">
                    Changed: {changedFields.join(', ')}
                </Typography>
                <Grid container spacing={1} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Active PAB price</Typography>
                        <Typography variant="body2">{formatMoney(activeProduct?.belowLinePrice)}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Draft PAB price</Typography>
                        <Typography variant="body2" fontWeight={700}>{formatMoney(draftProduct?.belowLinePrice)}</Typography>
                    </Grid>
                </Grid>
            </Alert>
        );
    };

    const renderDisplayChipSet = (label, chips = []) => {
        if (chips.length === 0) {
            return null;
        }

        return (
            <Box>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                    {sortedDisplayChips(chips).map((chip, index) => (
                        <Chip
                            key={`${chip.text}-${index}`}
                            label={chip.text || 'Untitled chip'}
                            size="small"
                            color={chipColor(chip.type)}
                            variant={chip.visible ? 'filled' : 'outlined'}
                        />
                    ))}
                </Stack>
            </Box>
        );
    };

    const renderHomePhoneProductCard = (product, groupName) => {
        const activeProduct = findActiveHomePhoneProduct(groupName, product.id);
        const changed = isHomePhoneDraftChanged(product, activeProduct);
        const display = getHomePhoneDisplayMetadata(product);
        const activeTopBadges = display.topBadges.filter((chip) => chip.visible);
        const hiddenTopBadges = display.topBadges.filter((chip) => !chip.visible);
        const activeDetailChips = display.detailChips.filter((chip) => chip.visible);
        const hiddenDetailChips = display.detailChips.filter((chip) => !chip.visible);

        return (
            <Card key={product.id} variant="outlined" sx={{ height: '100%', borderColor: changed ? 'warning.main' : undefined }}>
                <CardContent>
                    <Stack spacing={1.5}>
                        {renderProductCardSummary(product, () => openHomePhoneEditor(groupName, product))}
                        <Box>
                            <Typography variant="caption" color="text.secondary">Agent-facing chips</Typography>
                            {renderAgentFacingChips(product, display)}
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary">Admin status</Typography>
                            {renderKeyStatusChips(product, changed)}
                        </Box>
                        {renderDetailsPanel(
                            <>
                                <Typography variant="caption" color="text.secondary">Product key: {product.id}</Typography>
                                {renderStatusChips(product)}
                                {renderPriceFields(product)}
                                {display.cardDescription && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Draft card description</Typography>
                                        <Typography variant="body2">{display.cardDescription}</Typography>
                                    </Box>
                                )}
                                {renderDisplayChipSet('Active top badges', activeTopBadges)}
                                {renderDisplayChipSet('Hidden top badges', hiddenTopBadges)}
                                {renderDisplayChipSet('Active detail chips', activeDetailChips)}
                                {renderDisplayChipSet('Hidden detail chips', hiddenDetailChips)}
                                {renderHomePhoneActiveDraftDiff(product, activeProduct)}
                                {display.highlightNote && (
                                    <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
                                        {display.highlightNote}
                                    </Alert>
                                )}
                                {display.internalAgentNote && (
                                    <Typography variant="body2" color="text.secondary">
                                        Agent note: {display.internalAgentNote}
                                    </Typography>
                                )}
                                {product.note && (
                                    <Typography variant="body2" color="text.secondary">
                                        {product.note}
                                    </Typography>
                                )}
                                {product.emailDisplayName && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Customer-facing email name</Typography>
                                        <Typography variant="body2">{product.emailDisplayName}</Typography>
                                    </Box>
                                )}
                                {renderNotes('Customer-facing email bullets', product.emailBullets)}
                                {renderNotes('Internal notes', product.internalNotes)}
                                {renderNotes('Customer notes', product.customerNotes)}
                            </>
                        )}
                    </Stack>
                </CardContent>
            </Card>
        );
    };

    const renderHomePhoneProductGrid = (products = [], groupName) => (
        <Grid container spacing={2}>
            {sortProductsBySortOrder(products).map((product) => (
                <Grid item xs={12} md={6} xl={4} key={product.id}>
                    {renderHomePhoneProductCard(product, groupName)}
                </Grid>
            ))}
        </Grid>
    );

    const renderInternetProductCard = (product) => {
        const activeProduct = findActiveInternetProduct(product.id);
        const changed = isInternetDraftChanged(product, activeProduct);
        const display = getInternetDisplayMetadata(product);
        const activeTopBadges = display.topBadges.filter((chip) => chip.visible);
        const hiddenTopBadges = display.topBadges.filter((chip) => !chip.visible);
        const activeDetailChips = display.detailChips.filter((chip) => chip.visible);
        const hiddenDetailChips = display.detailChips.filter((chip) => !chip.visible);

        return (
            <Card key={product.id} variant="outlined" sx={{ height: '100%', borderColor: changed ? 'warning.main' : undefined }}>
                <CardContent>
                    <Stack spacing={1.5}>
                        {renderProductCardSummary(product, () => openInternetEditor(product))}
                        <Box>
                            <Typography variant="caption" color="text.secondary">Agent-facing chips</Typography>
                            {renderAgentFacingChips(product, display)}
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary">Admin status</Typography>
                            {renderKeyStatusChips(product, changed)}
                        </Box>
                        {renderDetailsPanel(
                            <>
                                <Typography variant="caption" color="text.secondary">Product key: {product.id}</Typography>
                                {renderStatusChips(product)}
                                {renderPriceFields(product)}
                                {display.cardDescription && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Draft card description</Typography>
                                        <Typography variant="body2">{display.cardDescription}</Typography>
                                    </Box>
                                )}
                                {renderDisplayChipSet('Active top badges', activeTopBadges)}
                                {renderDisplayChipSet('Hidden top badges', hiddenTopBadges)}
                                {renderDisplayChipSet('Active detail chips', activeDetailChips)}
                                {renderDisplayChipSet('Hidden detail chips', hiddenDetailChips)}
                                {renderInternetActiveDraftDiff(product, activeProduct)}
                                {display.highlightNote && (
                                    <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
                                        {display.highlightNote}
                                    </Alert>
                                )}
                                {display.internalAgentNote && (
                                    <Typography variant="body2" color="text.secondary">
                                        Agent note: {display.internalAgentNote}
                                    </Typography>
                                )}
                                {renderNotes('Customer-facing email bullets', product.emailBullets)}
                            </>
                        )}
                    </Stack>
                </CardContent>
            </Card>
        );
    };

    const renderInternetProductGrid = (products = []) => (
        <Grid container spacing={2}>
            {sortProductsBySortOrder(products).map((product) => (
                <Grid item xs={12} md={6} xl={4} key={product.id}>
                    {renderInternetProductCard(product)}
                </Grid>
            ))}
        </Grid>
    );

    const renderCatalogActiveDraftDiff = (draftProduct, activeProduct) => {
        const changedFields = getCatalogChangedFields(draftProduct, activeProduct);

        if (changedFields.length === 0) {
            return null;
        }

        return (
            <Alert severity="warning" variant="outlined" sx={{ py: 0.75 }}>
                <Typography variant="body2" fontWeight={700}>Draft differs from active</Typography>
                <Typography variant="caption" display="block">
                    Changed: {changedFields.join(', ')}
                </Typography>
                <Grid container spacing={1} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Active price</Typography>
                        <Typography variant="body2">{formatMoney(activeProduct?.price)}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Draft price</Typography>
                        <Typography variant="body2" fontWeight={700}>{formatMoney(draftProduct?.price)}</Typography>
                    </Grid>
                </Grid>
            </Alert>
        );
    };

    const renderCatalogProductCard = (scope, product, groupId = null) => {
        const activeProduct = findActiveCatalogProduct(scope, product.id, groupId);
        const changed = isCatalogDraftChanged(product, activeProduct);
        const display = getHomePhoneDisplayMetadata(product);
        const activeTopBadges = display.topBadges.filter((chip) => chip.visible);
        const hiddenTopBadges = display.topBadges.filter((chip) => !chip.visible);
        const activeDetailChips = display.detailChips.filter((chip) => chip.visible);
        const hiddenDetailChips = display.detailChips.filter((chip) => !chip.visible);

        return (
            <Card key={product.id} variant="outlined" sx={{ height: '100%', borderColor: changed ? 'warning.main' : undefined }}>
                <CardContent>
                    <Stack spacing={1.5}>
                        {renderProductCardSummary(product, () => openCatalogEditor(scope, product, groupId))}
                        <Box>
                            <Typography variant="caption" color="text.secondary">Agent-facing chips</Typography>
                            {renderAgentFacingChips(product, display)}
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary">Admin status</Typography>
                            {renderKeyStatusChips(product, changed)}
                        </Box>
                        {renderDetailsPanel(
                            <>
                                <Typography variant="caption" color="text.secondary">Product key: {product.id}</Typography>
                                {renderStatusChips(product)}
                                {renderPriceFields(product)}
                                {scope === 'mobility' && product.linePricing && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Line pricing rules</Typography>
                                        <Typography variant="body2">{formatMobilityLinePricingNote(product.linePricing, product.price)}</Typography>
                                    </Box>
                                )}
                                {product.type && <Typography variant="body2" color="text.secondary">Type: {product.type}</Typography>}
                                {product.category && <Typography variant="body2" color="text.secondary">Category: {product.category}</Typography>}
                                {product.term && <Typography variant="body2" color="text.secondary">Term: {product.term}</Typography>}
                                {product.devices && <Typography variant="body2" color="text.secondary">Included devices: {product.devices}</Typography>}
                                {product.note && <Typography variant="body2" color="text.secondary">{product.note}</Typography>}
                                {display.cardDescription && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Draft card description</Typography>
                                        <Typography variant="body2">{display.cardDescription}</Typography>
                                    </Box>
                                )}
                                {renderDisplayChipSet('Active top badges', activeTopBadges)}
                                {renderDisplayChipSet('Hidden top badges', hiddenTopBadges)}
                                {renderDisplayChipSet('Active detail chips', activeDetailChips)}
                                {renderDisplayChipSet('Hidden detail chips', hiddenDetailChips)}
                                {renderCatalogActiveDraftDiff(product, activeProduct)}
                                {display.highlightNote && (
                                    <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
                                        {display.highlightNote}
                                    </Alert>
                                )}
                                {display.internalAgentNote && (
                                    <Typography variant="body2" color="text.secondary">
                                        Agent note: {display.internalAgentNote}
                                    </Typography>
                                )}
                                {product.emailDisplayName && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Customer-facing email name</Typography>
                                        <Typography variant="body2">{product.emailDisplayName}</Typography>
                                    </Box>
                                )}
                                {renderNotes('Customer-facing email bullets', product.emailBullets)}
                                {renderNotes('Internal notes', product.internalNotes)}
                                {renderNotes('Customer notes', product.customerNotes)}
                            </>
                        )}
                    </Stack>
                </CardContent>
            </Card>
        );
    };

    const renderCatalogProductGrid = (scope, products = [], groupId = null) => (
        <Grid container spacing={2}>
            {sortProductsBySortOrder(products).map((product) => (
                <Grid item xs={12} md={6} xl={4} key={product.id}>
                    {renderCatalogProductCard(scope, product, groupId)}
                </Grid>
            ))}
        </Grid>
    );

    const renderTvDisplayActiveDraftDiff = (draftPackage, activePackage) => {
        const changedFields = getTvDisplayChangedFields(draftPackage, activePackage);

        if (changedFields.length === 0) {
            return null;
        }

        return (
            <Alert severity="warning" variant="outlined" sx={{ py: 0.75 }}>
                <Typography variant="body2" fontWeight={700}>Draft differs from active</Typography>
                <Typography variant="caption" display="block">
                    Changed: {changedFields.join(', ')}
                </Typography>
            </Alert>
        );
    };

    const renderTvDisplayPackageCard = (pkg) => {
        const activePackage = findActiveTvDisplayPackage(pkg.packageKey);
        const changed = isTvDisplayDraftChanged(pkg, activePackage);
        const livePrice = tvV2PriceByKey[pkg.packageKey];
        const visibleTopBadges = normalizeDisplayChipList(pkg.topBadges).filter((chip) => chip.visible !== false);
        const hiddenTopBadges = normalizeDisplayChipList(pkg.topBadges).filter((chip) => chip.visible === false);
        const visibleDetailChips = normalizeDisplayChipList(pkg.detailChips || pkg.chips).filter((chip) => chip.visible !== false);
        const hiddenDetailChips = normalizeDisplayChipList(pkg.detailChips || pkg.chips).filter((chip) => chip.visible === false);

        return (
            <Card key={pkg.packageKey} variant="outlined" sx={{ height: '100%', borderColor: changed ? 'warning.main' : undefined }}>
                <CardContent>
                    <Stack spacing={1.5}>
                        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="h6">{pkg.displayNameOverride || pkg.packageKey}</Typography>
                                <Typography variant="caption" color="text.secondary">Sort order {pkg.sortOrder ?? 'N/A'}</Typography>
                            </Box>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-end', sm: 'center' }}>
                                <Box textAlign="right">
                                    <Typography variant="h6" color="primary.main">
                                        {livePrice === undefined ? 'TV V2' : formatMoney(livePrice)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">Live TV V2 price</Typography>
                                </Box>
                                <Button variant="outlined" size="small" onClick={() => openTvDisplayEditor(pkg)}>
                                    Edit Draft
                                </Button>
                            </Stack>
                        </Stack>
                        <Box>
                            <Typography variant="caption" color="text.secondary">Agent-facing TV card preview</Typography>
                            {visibleTopBadges.length > 0 ? (
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                    {visibleTopBadges.map((chip, index) => (
                                        <Chip
                                            key={`${chip.text}-${index}`}
                                            label={chip.text || 'Untitled badge'}
                                            size="small"
                                            color={chipColor(chip.type)}
                                            variant="outlined"
                                        />
                                    ))}
                                </Stack>
                            ) : (
                                <Typography variant="caption" color="text.secondary">No top badges configured.</Typography>
                            )}
                            {visibleDetailChips.length > 0 && (
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                                    {visibleDetailChips.map((chip, index) => (
                                        <Chip
                                            key={`${chip.text}-${index}`}
                                            label={chip.text || 'Untitled chip'}
                                            size="small"
                                            variant="outlined"
                                        />
                                    ))}
                                </Stack>
                            )}
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary">Admin status</Typography>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                                <Chip label={pkg.packageKey} size="small" variant="outlined" />
                                <Chip label={changed ? 'Draft changed' : 'Draft matches active'} size="small" color={changed ? 'warning' : 'default'} variant="outlined" />
                                <Chip label={pkg.visibleInManualFallback === false ? 'Manual hidden' : 'Manual visible'} size="small" color={pkg.visibleInManualFallback === false ? 'warning' : 'success'} variant="outlined" />
                                <Chip label={pkg.visibleInAgentBuilder === false ? 'Agent hidden' : 'Agent-visible'} size="small" color={pkg.visibleInAgentBuilder === false ? 'warning' : 'success'} variant="outlined" />
                            </Stack>
                        </Box>
                        {renderDetailsPanel(
                            <>
                                <Typography variant="caption" color="text.secondary">TV package key: {pkg.packageKey}</Typography>
                                <Typography variant="body2" color="text.secondary">PVR behavior: {pkg.pvrMode || 'optional'}{pkg.pvrMode === 'optional' ? ` (${formatMoney(pkg.pvrPrice)})` : ''}</Typography>
                                <Typography variant="body2" color="text.secondary">Manual fallback: {formatBoolean(pkg.visibleInManualFallback !== false)}</Typography>
                                <Typography variant="body2" color="text.secondary">Agent builder: {formatBoolean(pkg.visibleInAgentBuilder !== false)}</Typography>
                                <Typography variant="body2" color="text.secondary">Admin builder: {formatBoolean(pkg.visibleInAdminBuilder !== false)}</Typography>
                                <Typography variant="body2" color="text.secondary">Customer email: {formatBoolean(pkg.customerEmailEligible !== false)}</Typography>
                                <Typography variant="body2" color="text.secondary">Email regular/reference: {formatMoney(pkg.emailRegularReferencePrice)}</Typography>
                                {pkg.cardDescription && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Agent card note</Typography>
                                        <Typography variant="body2">{pkg.cardDescription}</Typography>
                                    </Box>
                                )}
                                {pkg.quoteSummaryNote && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Quote Summary note</Typography>
                                        <Typography variant="body2">{pkg.quoteSummaryNote}</Typography>
                                    </Box>
                                )}
                                {renderDisplayChipSet('Hidden top badges', hiddenTopBadges)}
                                {renderDisplayChipSet('Hidden detail chips', hiddenDetailChips)}
                                {renderTvDisplayActiveDraftDiff(pkg, activePackage)}
                                {pkg.customerEmailName && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Customer-facing email name</Typography>
                                        <Typography variant="body2">{pkg.customerEmailName}</Typography>
                                    </Box>
                                )}
                                {renderNotes('Customer-facing email bullets', pkg.customerEmailBullets)}
                                {pkg.internalAgentNote && (
                                    <Typography variant="body2" color="text.secondary">
                                        Agent note: {pkg.internalAgentNote}
                                    </Typography>
                                )}
                            </>
                        )}
                    </Stack>
                </CardContent>
            </Card>
        );
    };

    const renderTvDisplayPackageGrid = (packages = []) => (
        <Grid container spacing={2}>
            {sortProductsBySortOrder(packages).map((pkg) => (
                <Grid item xs={12} md={6} xl={4} key={pkg.packageKey}>
                    {renderTvDisplayPackageCard(pkg)}
                </Grid>
            ))}
        </Grid>
    );

    const renderOverview = () => (
        <Stack spacing={3}>
            <Grid container spacing={2}>
                {[
                    ['Internet products', counts.internet],
                    ['Home Phone items', counts.homePhone],
                    ['Mobility products', counts.mobility],
                    ['Security products', counts.security],
                    ['Add-ons', counts.addons]
                ].map(([label, value]) => (
                    <Grid item xs={12} sm={6} md={4} lg={2.4} key={label}>
                        <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                            <Typography variant="h4" color="primary.main">{value}</Typography>
                            <Typography variant="body2" color="text.secondary">{label}</Typography>
                        </Paper>
                    </Grid>
                ))}
            </Grid>

            <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                    <Typography variant="h6">Active Config Source</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                            <Typography variant="caption" color="text.secondary">Status</Typography>
                            <Typography variant="body2" fontWeight={700}>{config?.meta?.status}</Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Typography variant="caption" color="text.secondary">Mode</Typography>
                            <Typography variant="body2" fontWeight={700}>{config?.meta?.mode}</Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Typography variant="caption" color="text.secondary">Editable</Typography>
                            <Typography variant="body2" fontWeight={700}>{formatBoolean(config?.meta?.editable)}</Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Typography variant="caption" color="text.secondary">Draft enabled</Typography>
                            <Typography variant="body2" fontWeight={700}>{formatBoolean(config?.meta?.draftEnabled)}</Typography>
                        </Grid>
                    </Grid>
                    <Typography variant="body2" color="text.secondary">
                        Source: {config?.meta?.source}
                    </Typography>
                    <Alert severity="warning" variant="outlined">
                        Bundle Builder reads active Product Pricing Manager config for non-TV categories and TV display metadata with fallback. TV pricing stays in TV V2.
                    </Alert>
                </Stack>
            </Paper>

            {renderStorageStatus()}

            <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1}>
                    <Typography variant="h6">TV V2 Source of Truth</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {config?.tvReference?.duplicationRule}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip label={config?.tvReference?.pricingSource} color="primary" variant="outlined" />
                        <Chip label={config?.tvReference?.recommenderSource} variant="outlined" />
                        <Chip label={config?.tvReference?.status} color="success" variant="outlined" />
                    </Stack>
                </Stack>
            </Paper>

            <Grid container spacing={2}>
                {futureControlCards.map((card) => (
                    <Grid item xs={12} md={6} key={card.title}>
                        <Card variant="outlined" sx={{ height: '100%' }}>
                            <CardContent>
                                <Stack spacing={1}>
                                    <Typography variant="h6">{card.title}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {card.body}
                                    </Typography>
                                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                        {card.chips.map((chip) => (
                                            <Chip key={chip} label={chip} size="small" variant="outlined" />
                                        ))}
                                    </Stack>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {renderFutureControls()}
        </Stack>
    );

    const renderWorkflow = (scope, title, description, children = null) => (
        <PublishControls
            title={title}
            description={description}
            scope={scope}
            scopeLabel={scopeLabel(scope)}
            draftConfig={draftConfig}
            validating={validatingDraft}
            onValidate={validateDraftConfig}
            onPublish={openPublishDialog}
            validation={draftValidation}
            showAllWarnings={showAllValidationWarnings}
            onToggleWarnings={() => setShowAllValidationWarnings((current) => !current)}
        >
            {children}
        </PublishControls>
    );

    const renderInternetWorkflow = () => renderWorkflow(
        'internet',
        'Internet Draft Workflow',
        'Validate or publish Internet draft changes. Rollback is managed in Version History. Sort order controls the order agents see after publish.'
    );

    const renderInternet = () => (
        <Stack spacing={3}>
            {renderDraftMessages()}
            {renderCategoryDraftStatus('internet')}
            {renderInternetWorkflow()}
            <Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" mb={1}>
                    <Typography variant="h6">Internet Products</Typography>
                    <Button variant="outlined" size="small" onClick={openNewInternetEditor}>
                        Add Product
                    </Button>
                </Stack>
                {renderInternetProductGrid(getInternetProducts(draftConfig || config))}
            </Box>
        </Stack>
    );

    const renderHomePhoneWorkflow = () => renderWorkflow(
        'homePhone',
        'Home Phone Draft Workflow',
        'Validate or publish Home Phone draft changes. Rollback is managed in Version History. Sort order controls the order agents see after publish.'
    );

    const renderHomePhone = () => (
        <Stack spacing={3}>
            {renderDraftMessages()}
            {renderCategoryDraftStatus('homePhone')}
            {renderHomePhoneWorkflow()}
            <Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" mb={1}>
                    <Typography variant="h6">Home Phone Plans</Typography>
                    <Button variant="outlined" size="small" onClick={() => openNewHomePhoneEditor('plans')}>
                        Add Product
                    </Button>
                </Stack>
                {renderHomePhoneProductGrid(getHomePhoneGroup(draftConfig || config, 'plans'), 'plans')}
            </Box>
            <Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" mb={1}>
                    <Typography variant="h6">Long-distance Add-ons</Typography>
                    <Button variant="outlined" size="small" onClick={() => openNewHomePhoneEditor('longDistanceAddons')}>
                        Add Product
                    </Button>
                </Stack>
                {renderHomePhoneProductGrid(getHomePhoneGroup(draftConfig || config, 'longDistanceAddons'), 'longDistanceAddons')}
            </Box>
        </Stack>
    );

    const renderCatalogWorkflow = (scope) => renderWorkflow(
        scope,
        `${scopeLabel(scope)} Draft Workflow`,
        `Validate or publish ${scopeLabel(scope)} draft changes. Rollback is managed in Version History. Sort order controls the order agents see after publish.`
    );

    const renderMobilityManagement = () => {
        const products = draftConfig?.products?.mobility?.products || config?.products?.mobility?.products || [];
        const filteredProducts = mobilityAdminFilter === 'all'
            ? products
            : products.filter((product) => getMobilityAdminGroup(product) === mobilityAdminFilter);

        return (
            <Stack spacing={3}>
                {renderDraftMessages()}
                {renderCategoryDraftStatus('mobility')}
                {renderCatalogWorkflow('mobility')}
                <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={1.5}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
                            <Box>
                                <Typography variant="h6">Mobility Products</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Filter by the same high-level groups agents care about. EPP/RBC plans are grouped under TELUS EPP.
                                </Typography>
                            </Box>
                            <Button variant="outlined" size="small" onClick={() => openNewCatalogEditor('mobility')}>
                                Add Product
                            </Button>
                        </Stack>
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                            {mobilityAdminFilters.map((filter) => (
                                <Chip
                                    key={filter.id}
                                    label={filter.label}
                                    color={mobilityAdminFilter === filter.id ? 'primary' : 'default'}
                                    variant={mobilityAdminFilter === filter.id ? 'filled' : 'outlined'}
                                    onClick={() => setMobilityAdminFilter(filter.id)}
                                />
                            ))}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                            Showing {filteredProducts.length} of {products.length} Mobility products.
                        </Typography>
                    </Stack>
                </Paper>
                {filteredProducts.length > 0 ? (
                    renderCatalogProductGrid('mobility', filteredProducts)
                ) : (
                    <Alert severity="info" variant="outlined">
                        No Mobility products match this filter.
                    </Alert>
                )}
            </Stack>
        );
    };

    const renderSecurityManagement = () => (
        <Stack spacing={3}>
            {renderDraftMessages()}
            {renderCategoryDraftStatus('security')}
            {renderCatalogWorkflow('security')}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
                <Typography variant="h6">Security Products</Typography>
                <Button variant="outlined" size="small" onClick={() => openNewCatalogEditor('security')}>
                    Add Product
                </Button>
            </Stack>
            {renderCatalogProductGrid('security', draftConfig?.products?.security?.products || config?.products?.security?.products || [])}
        </Stack>
    );

    const renderAddons = () => (
        <Stack spacing={3}>
            {renderDraftMessages()}
            {renderCategoryDraftStatus('addons')}
            {renderCatalogWorkflow('addons')}
            {((draftConfig || config)?.products?.addons?.groups || []).map((group) => (
                <Box key={group.id}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" mb={1}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="h6">{group.title}</Typography>
                            <Chip label={`Sort ${group.sortOrder}`} size="small" variant="outlined" />
                        </Stack>
                        <Button variant="outlined" size="small" onClick={() => openNewCatalogEditor('addons', group.id)}>
                            Add Product
                        </Button>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" mb={1.5}>
                        {group.helperText}
                    </Typography>
                    {renderCatalogProductGrid('addons', group.items || [], group.id)}
                </Box>
            ))}
        </Stack>
    );

    const renderDiscountRules = () => (
        <Grid container spacing={2}>
            {Object.entries(config?.discountRules || {}).map(([key, rule]) => (
                <Grid item xs={12} md={6} key={key}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                            <Stack spacing={1}>
                                <Typography variant="h6">{rule.label}</Typography>
                                <Typography variant="caption" color="text.secondary">{key}</Typography>
                                {rule.meaning && <Typography variant="body2">{rule.meaning}</Typography>}
                                {rule.amount !== undefined && (
                                    <Typography variant="body2" fontWeight={700}>Amount: {formatMoney(rule.amount)}</Typography>
                                )}
                                {rule.defaultEnabled !== undefined && (
                                    <Typography variant="body2">Default enabled: {formatBoolean(rule.defaultEnabled)}</Typography>
                                )}
                                {rule.affects && renderNotes('Affects', rule.affects)}
                                {rule.activeStates && renderNotes('Active states', rule.activeStates)}
                                {rule.eligibility && (
                                    <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
                                        {rule.eligibility}
                                    </Alert>
                                )}
                                {rule.customerEmailVisibility && (
                                    <Typography variant="body2" color="text.secondary">
                                        Customer email: {rule.customerEmailVisibility}
                                    </Typography>
                                )}
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
            ))}
        </Grid>
    );

    const renderEmailShellWorkflow = () => renderWorkflow(
        'emailShell',
        'Email Shell Draft Workflow',
        'Publishing updates the active Bundle Builder email preview shell only. It does not send emails or modify existing templates.',
        <>
            <Button variant="outlined" onClick={openEmailShellEditor} disabled={!draftConfig}>
                Edit Email Shell Draft
            </Button>
            <Button variant="outlined" onClick={() => setEmailShellPreviewOpen(true)} disabled={!draftConfig}>
                Preview Email Shell
            </Button>
        </>
    );

    const renderEmailShellSummary = (title, shell, marker = null) => (
        <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Stack spacing={1.25}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="h6">{title}</Typography>
                    {marker}
                </Stack>
                <Grid container spacing={1.5}>
                    {[
                        ['Store', shell.storeName],
                        ['Address', [shell.storeAddressLine1, shell.storeAddressLine2].filter(Boolean).join(', ')],
                        ['Phone', shell.storePhone],
                        ['Intro headline', shortText(shell.introHeadline, 80)],
                        ['Intro subheadline', shortText(shell.introSubheadline, 80)],
                        ['Agent contact', shortText(shell.agentContactHeadline, 80)]
                    ].map(([label, value]) => (
                        <Grid item xs={12} md={6} key={label}>
                            <Typography variant="caption" color="text.secondary">{label}</Typography>
                            <Typography variant="body2">{value || 'N/A'}</Typography>
                        </Grid>
                    ))}
                </Grid>
                <Divider />
                <Typography variant="subtitle2">Image and link settings</Typography>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {emailShellImageFields.slice(0, 8).map((field) => (
                        <Chip key={field.key} label={shell[field.key] ? field.label : `${field.label} missing`} size="small" color={shell[field.key] ? 'success' : 'warning'} variant="outlined" />
                    ))}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                    Footer/legal text and full image URL list are editable in the draft modal.
                </Typography>
            </Stack>
        </Paper>
    );

    const renderEmailOutput = () => (
        <Stack spacing={3}>
            {renderDraftMessages()}
            {renderCategoryDraftStatus('emailShell')}
            <Alert severity="warning" variant="outlined">
                Email Shell Settings control Bundle Builder preview HTML only. Real Send Offer, saved templates, and sent history are not connected to this page.
            </Alert>
            {renderEmailShellWorkflow()}
            <Grid container spacing={2}>
                <Grid item xs={12} lg={6}>
                    {renderEmailShellSummary('Active Email Shell', activeEmailShell)}
                </Grid>
                <Grid item xs={12} lg={6}>
                    {renderEmailShellSummary(
                        'Draft Email Shell',
                        draftEmailShell,
                        emailShellDraftChanged ? <Chip label="Draft changed" size="small" color="warning" variant="outlined" /> : <Chip label="Matches active" size="small" color="success" variant="outlined" />
                    )}
                </Grid>
            </Grid>
            <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1}>
                    <Typography variant="h6">Email Output Rules</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {config?.emailOutputSettings?.shellStatus}
                    </Typography>
                    <Chip
                        label={config?.emailOutputSettings?.agentHtmlEditingAllowed ? 'Agent HTML editing allowed' : 'Agent HTML editing disabled'}
                        color={config?.emailOutputSettings?.agentHtmlEditingAllowed ? 'warning' : 'success'}
                        variant="outlined"
                        sx={{ width: 'fit-content' }}
                    />
                    {renderNotes('Required placeholders', config?.emailOutputSettings?.placeholders)}
                    {renderNotes('Product fields', config?.emailOutputSettings?.productFields)}
                </Stack>
            </Paper>
        </Stack>
    );

    const renderTvReference = () => (
        <Stack spacing={2}>
            <Alert severity="info">
                TV pricing and recommendations are controlled by existing TV V2 tools. This page intentionally does not duplicate TV prices.
            </Alert>
            <Alert severity="success" variant="outlined">
                Bundle Builder TV recommender uses TV V2 API totals from <strong>/api/tv-packages-v2/recommend</strong>. After TV pricing changes, rerun Get Recommendation and click Use Best Value in Bundle.
            </Alert>
            <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                    <Typography variant="h6">TV V2 Reference</Typography>
                    <Typography variant="body2">
                        Pricing source: <strong>{config?.tvReference?.pricingSource}</strong>
                    </Typography>
                    <Typography variant="body2">
                        Recommender source: <strong>{config?.tvReference?.recommenderSource}</strong>
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {tvToolLinks.map((link) => (
                            <Button key={link.href} variant="outlined" href={link.href}>
                                {link.label}
                            </Button>
                        ))}
                    </Stack>
                    <Divider />
                    <Typography variant="subtitle2">Endpoints</Typography>
                    {Object.entries(config?.tvReference?.endpoints || {}).map(([key, value]) => (
                        <Typography key={key} variant="body2" color="text.secondary">
                            {key}: {value}
                        </Typography>
                    ))}
                    <Typography variant="subtitle2">Existing tools</Typography>
                    <List dense disablePadding>
                        {(config?.tvReference?.tools || []).map((tool) => (
                            <ListItem key={tool.id} disableGutters>
                                <ListItemText
                                    primary={tool.label}
                                    secondary={`View: ${tool.view}`}
                                    primaryTypographyProps={{ variant: 'body2', fontWeight: 700 }}
                                />
                            </ListItem>
                        ))}
                    </List>
                    <Alert severity="warning" variant="outlined">
                        {config?.tvReference?.duplicationRule}
                    </Alert>
                </Stack>
            </Paper>
        </Stack>
    );

    const renderTvDisplaySettings = () => (
        <Stack spacing={2}>
            {renderDraftMessages()}
            <Alert severity="info">
                TV Display Settings control how TV packages appear in Bundle Builder cards, Quote Summary, and customer email rows. TV promo/current prices still come from TV V2.
            </Alert>
            <Alert severity="warning" variant="outlined">
                Do not store active TV prices here. Use TV Package Pricing & Channels V2 for TV prices and recommendations.
            </Alert>
            <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
                    <Box>
                        <Typography variant="h6">TV Display Metadata</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Draft editing, validation, publish, and rollback are enabled for TV display metadata only.
                        </Typography>
                    </Box>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <Button variant="outlined" onClick={validateDraftConfig} disabled={validatingDraft || !draftConfig}>
                            {validatingDraft ? 'Validating...' : 'Validate Draft'}
                        </Button>
                        <Button variant="contained" onClick={() => openPublishDialog('tvDisplaySettings')} disabled={!hasDraftChangesForScope('tvDisplaySettings')}>
                            Publish TV Display Draft
                        </Button>
                    </Stack>
                </Stack>
            </Paper>
            {hasDraftChangesForScope('tvDisplaySettings') && (
                <Alert severity="warning" variant="outlined">
                    TV Display Settings draft differs from active. Bundle Builder will keep using active TV display metadata until this scope is published.
                </Alert>
            )}
            <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                        <Typography variant="subtitle2" color="text.secondary">TV V2 live prices loaded</Typography>
                        <Typography variant="h5">{normalizeArray(tvV2Pricing.packages).length}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Read-only display from /api/tv-packages-v2/pricing.
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                        <Typography variant="subtitle2" color="text.secondary">Display rows</Typography>
                        <Typography variant="h5">{getTvDisplayPackages(draftConfig || config).length}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Package keys reference TV V2 or approved manual fallback packages.
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                        <Typography variant="subtitle2" color="text.secondary">Pricing source</Typography>
                        <Typography variant="body1" fontWeight={700}>TV V2 only</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Product Pricing Manager stores display notes, PVR behavior, chips, and email bullets only.
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>
            {renderTvDisplayPackageGrid(getTvDisplayPackages(draftConfig || config))}
        </Stack>
    );

    const renderVersionHistory = () => (
        <VersionHistory
            versions={sortedVersions}
            showMore={showMoreVersions}
            onToggleMore={() => setShowMoreVersions((current) => !current)}
            scope={versionHistoryScope}
            onScopeChange={setVersionHistoryScope}
            scopeLabel={scopeLabel}
            onRollback={openRollbackDialog}
            formatDateTime={formatDateTime}
            shortText={shortText}
            feedback={renderDraftMessages()}
        />
    );

    const renderActiveTab = () => {
        if (!config) {
            return null;
        }

        switch (activeTab) {
            case 'overview':
                return renderOverview();
            case 'internet':
                return (
                    <Stack spacing={2}>
                        {renderSectionIntro('Internet Products', 'Draft editing, validation, and publish are enabled for Internet products. Version rollback lives in the Version History tab.', ['Draft save', 'Validate', 'Publish'])}
                        {renderInternet()}
                    </Stack>
                );
            case 'tv':
                return renderTvReference();
            case 'tvDisplay':
                return (
                    <Stack spacing={2}>
                        {renderSectionIntro('TV Display Settings', 'Draft editing, validation, and publish are enabled for TV display metadata only. TV prices and recommendation logic remain controlled by TV V2.', ['Display only', 'No TV prices', 'Draft preview'])}
                        {renderTvDisplaySettings()}
                    </Stack>
                );
            case 'homePhone':
                return (
                    <Stack spacing={2}>
                        {renderSectionIntro('Home Phone', 'Draft editing, validation, and publish are enabled for Home Phone plans and long-distance add-ons. Version rollback lives in the Version History tab.', ['Draft save', 'Validate', 'Publish'])}
                        {renderHomePhone()}
                    </Stack>
                );
            case 'mobility':
                return (
                    <Stack spacing={2}>
                        {renderSectionIntro('Mobility', 'Draft editing, validation, and publish are enabled for Mobility products. Mobility remains separate from Home Services email output unless a future approved flow is added.', ['Draft save', 'Validate', 'Publish'])}
                        {renderMobilityManagement()}
                    </Stack>
                );
            case 'security':
                return (
                    <Stack spacing={2}>
                        {renderSectionIntro('Security', 'Draft editing, validation, and publish are enabled for Security products. Security stays quote-only and separate-flow-only in the current Bundle Builder rules.', ['Draft save', 'Validate', 'Publish'])}
                        {renderSecurityManagement()}
                    </Stack>
                );
            case 'addons':
                return (
                    <Stack spacing={2}>
                        {renderSectionIntro('Add-ons', 'Draft editing, validation, and publish are enabled for add-on products. Add-ons remain quote-only unless later approved.', ['Draft save', 'Validate', 'Publish'])}
                        {renderAddons()}
                    </Stack>
                );
            case 'discounts':
                return (
                    <Stack spacing={2}>
                        {renderSectionIntro('Discount Rules', 'Read-only view of the pricing rules Bundle Builder currently applies for PAB, TELUS/Koodo mobility discount, EPP, and one-time M&H credit eligibility.', ['PAB', 'M&H', 'EPP', '$50 credit'])}
                        {renderDiscountRules()}
                    </Stack>
                );
            case 'email':
                return (
                    <Stack spacing={2}>
                        {renderSectionIntro('Email Shell Settings', 'Draft editing, validation, publish, and rollback are enabled for the Bundle Builder preview shell. Existing templates and Send Offer are not connected.', ['Draft save', 'Validate', 'Publish', 'Preview shell'])}
                        {renderEmailOutput()}
                    </Stack>
                );
            case 'versions':
                return renderVersionHistory();
            default:
                return renderOverview();
        }
    };

    return renderActiveTab();
}

export default ProductPricingActiveTab;
